import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, MenuItem, Paper, Select, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'

const CASE_ID = 'DS/2026/HĐMB-ABC-012'

type ChatTone = 'ai' | 'user'
type ChatMsg = { id: string; tone: ChatTone; text: string }

export function WorkspaceIntegrationTab() {
  const theme = useTheme()
  const [syncState, setSyncState] = useState<'idle' | 'syncing' | 'done'>('idle')
  const [scenario, setScenario] = useState('Phiên hòa giải lần 1')
  const [aiRole, setAiRole] = useState<1 | 2>(1)
  const [trialOpen, setTrialOpen] = useState(false)
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([])
  const chatTurnRef = useRef(0)

  useEffect(() => {
    if (syncState === 'syncing') {
      const t = window.setTimeout(() => setSyncState('done'), 1500)
      return () => window.clearTimeout(t)
    }
  }, [syncState])

  const syncAll = () => {
    setSyncState('syncing')
  }

  const startTrial = () => {
    setTrialOpen((v) => !v)
  }

  const script = useMemo<ChatMsg[]>(() => {
    const who = aiRole === 1 ? 'AI Bị đơn' : 'AI Thẩm phán'
    const baseIntro: ChatMsg[] = [
      {
        id: 'sys-brief',
        tone: 'ai',
        text:
          'Tóm tắt hồ sơ: tranh chấp HĐMB căn hộ ABC. Mốc hiệu lực hợp đồng có dấu hiệu mâu thuẫn; phát sinh khiếu nại chậm bàn giao; chứng cứ gồm HĐMB, email xác nhận, biên bản làm việc và chứng từ thanh toán.',
      },
    ]

    if (scenario === 'Kiểm tra chứng cứ') {
      return [
        ...baseIntro,
        { id: 'q1', tone: 'ai', text: `${who}: Nguyên đơn xuất trình 3 nhóm chứng cứ cốt lõi nào để chứng minh “đến hạn bàn giao” và “vi phạm nghĩa vụ”?` },
        {
          id: 'a1',
          tone: 'user',
          text:
            'Tôi xuất trình: (1) Email/biên bản thể hiện các bên đã thống nhất mốc hiệu lực và/hoặc thời hạn bàn giao; (2) Sao kê/phiếu thu chứng minh thanh toán đúng tiến độ; (3) Biên bản làm việc/biên bản bàn giao (hoặc văn bản từ chối bàn giao) thể hiện bị đơn chậm thực hiện.',
        },
        {
          id: 'q2',
          tone: 'ai',
          text:
            `${who}: Bị đơn sẽ phản biện “mốc hiệu lực” bằng điều khoản nào và bạn phản hồi ra sao để khóa mốc hiệu lực theo chứng cứ ưu tiên?`,
        },
        {
          id: 'a2',
          tone: 'user',
          text:
            'Tôi ưu tiên chứng cứ thể hiện sự thừa nhận/ý chí thống nhất của các bên (email xác nhận, biên bản làm việc) và hành vi thực hiện (nhận tiền theo mốc). Tôi lập timeline đối chiếu từng tài liệu để chứng minh mốc hiệu lực thực tế được áp dụng.',
        },
        {
          id: 'q3',
          tone: 'ai',
          text:
            `${who}: Nếu Tòa không chấp nhận “phạt vi phạm” do điều khoản quá cao/khó bảo vệ, phương án thay thế của nguyên đơn là gì?`,
        },
        {
          id: 'a3',
          tone: 'user',
          text:
            'Chuyển trọng tâm sang bồi thường thiệt hại thực tế (chi phí thuê nhà, lãi vay, chi phí phát sinh) kèm chứng từ; đồng thời yêu cầu lãi chậm trả (nếu có cơ sở) và/hoặc buộc thực hiện nghĩa vụ bàn giao.',
        },
      ]
    }

    if (scenario === 'Phiên xét xử sơ thẩm') {
      return [
        ...baseIntro,
        { id: 'open', tone: 'ai', text: `${who}: Mời nguyên đơn trình bày yêu cầu khởi kiện và căn cứ chính.` },
        {
          id: 'claim',
          tone: 'user',
          text:
            'Tôi đề nghị: (i) Xác định bị đơn vi phạm nghĩa vụ bàn giao đúng hạn; (ii) Buộc bị đơn bàn giao căn hộ trong thời hạn Tòa ấn định, hoặc bồi thường thiệt hại nếu không thể bàn giao; (iii) Ghi nhận/áp dụng cơ chế bồi thường theo thiệt hại thực tế có chứng cứ; (iv) Các chi phí tố tụng theo quy định.',
        },
        {
          id: 'cross1',
          tone: 'ai',
          text:
            `${who}: Bị đơn cho rằng nguyên đơn chưa hoàn tất nghĩa vụ thanh toán nên chưa phát sinh nghĩa vụ bàn giao. Bạn đối đáp bằng chứng cứ nào?`,
        },
        {
          id: 'rebut1',
          tone: 'user',
          text:
            'Tôi đối chiếu tiến độ thanh toán trong hợp đồng/phụ lục với sao kê, phiếu thu. Đồng thời trích email/biên bản làm việc thể hiện bị đơn vẫn cam kết bàn giao theo mốc dù đã nhận tiền theo các đợt.',
        },
        {
          id: 'cross2',
          tone: 'ai',
          text: `${who}: Nếu có mâu thuẫn Điều 7 và Điều 12, vì sao Tòa nên ưu tiên cách hiểu của nguyên đơn?`,
        },
        {
          id: 'rebut2',
          tone: 'user',
          text:
            'Vì có chứng cứ ngoài hợp đồng cho thấy các bên đã thống nhất mốc hiệu lực (email xác nhận), và đã có hành vi thực hiện theo mốc đó (thu tiền, hẹn bàn giao). Mâu thuẫn điều khoản được giải thích theo ý chí chung của các bên và thực tiễn thực hiện.',
        },
        {
          id: 'close',
          tone: 'ai',
          text:
            `${who}: Kết luận phiên: nguyên đơn cần nộp bảng timeline + bảng thanh toán đối chiếu, kèm 3 chứng cứ then chốt; ưu tiên yêu cầu buộc thực hiện nghĩa vụ, dự phòng bồi thường thiệt hại.`,
        },
      ]
    }

    // default: Phiên hòa giải lần 1
    return [
      ...baseIntro,
      {
        id: 'm1',
        tone: 'ai',
        text: `${who}: Mục tiêu hòa giải là thu hẹp tranh chấp: mốc hiệu lực, tiến độ thanh toán, và thời hạn bàn giao. Nguyên đơn đề xuất phương án nào?`,
      },
      {
        id: 'm2',
        tone: 'user',
        text:
          'Tôi đề xuất: thống nhất 01 mốc hiệu lực theo email/biên bản; ấn định lịch bàn giao mới; nếu trễ thì bồi thường chi phí phát sinh theo chứng từ và giảm/khấu trừ một phần nghĩa vụ tài chính.',
      },
      {
        id: 'm3',
        tone: 'ai',
        text:
          `${who}: Bị đơn có thể yêu cầu điều kiện kèm theo. Bạn chấp nhận điều kiện nào và loại trừ điều kiện nào để bảo vệ quyền lợi?`,
      },
      {
        id: 'm4',
        tone: 'user',
        text:
          'Tôi chấp nhận điều kiện minh bạch: đối soát công nợ theo sao kê/phiếu thu. Tôi loại trừ điều kiện bất lợi như gia hạn không giới hạn hoặc miễn trừ trách nhiệm bàn giao khi đã nhận tiền theo tiến độ.',
      },
      {
        id: 'm5',
        tone: 'ai',
        text:
          `${who}: Kết thúc: đề nghị hai bên chốt 01 mốc hiệu lực và ký biên bản hòa giải ghi nhận lịch bàn giao + cơ chế bồi thường nếu trễ (kèm cách tính rõ ràng).`,
      },
    ]
  }, [aiRole, scenario])

  useEffect(() => {
    if (!trialOpen) return
    // reset and replay script
    chatTurnRef.current = 0
    setChatMessages([])
    const iv = window.setInterval(() => {
      const idx = chatTurnRef.current
      if (idx >= script.length) {
        window.clearInterval(iv)
        return
      }
      const next = script[idx]
      chatTurnRef.current += 1
      setChatMessages((prev) => [...prev, next])
    }, 900)
    return () => window.clearInterval(iv)
  }, [trialOpen, script])

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="body2" fontWeight={700}>
            Workspace: Vụ án {CASE_ID}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Tranh chấp HĐMB căn hộ · Chậm bàn giao + mốc hiệu lực hợp đồng mâu thuẫn
          </Typography>
        </Box>
        <Button variant="contained" size="small" onClick={syncAll} disabled={syncState === 'syncing'}>
          Đồng bộ lên E-Court
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          mb: 2,
          boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary' }}>
          TRẠNG THÁI KẾT NỐI MODULE
        </Typography>
        <Stack direction="row" alignItems="flex-start" spacing={0} sx={{ width: '100%', overflowX: 'auto' }}>
          <PipeNode icon="📄" title="Phân tích tài liệu" done />
          <PipeLine done />
          <PipeNode icon="✍️" title="Soạn thảo AI" done />
          <PipeLine done />
          <PipeNode icon="🎯" title="Chiến lược AI" done />
          <PipeLine done={syncState === 'done'} syncing={syncState === 'syncing'} />
          <PipeNode icon="🏛️" title="E-Court" done={syncState === 'done'} pending={syncState !== 'done'} syncing={syncState === 'syncing'} />
        </Stack>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary' }}>
            TÓM TẮT DỮ LIỆU TỔNG HỢP
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
            <StatCard value="3" label="Tài liệu đã phân tích" />
            <StatCard value="2" label="Mâu thuẫn phát hiện" />
            <StatCard value="58%" label="Dự báo thắng một phần" />
            <StatCard value="Bản nháp #3" label="Đơn khởi kiện/Trình bày" />
          </Box>
          <Box sx={{ borderTop: 1, borderColor: 'divider', my: 1.25 }} />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Thao tác nhanh:
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <Button size="small" variant="outlined">
              Xuất hồ sơ
            </Button>
            <Button size="small" variant="outlined">
              Báo cáo tổng hợp
            </Button>
            <Button size="small" variant="outlined">
              In toàn bộ
            </Button>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            borderRadius: 2,
            boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'primary.dark' }}>
              Phiên tòa giả lập (Mock Trial)
            </Typography>
            <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: alpha(theme.palette.info.main, 0.12), color: 'primary.dark', fontSize: 10, fontWeight: 700 }}>
              BETA
            </Box>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Dữ liệu từ các module đã sẵn sàng. Chọn kịch bản và bắt đầu luyện tập phiên.
          </Typography>

          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Kịch bản
          </Typography>
          <Select fullWidth size="small" value={scenario} onChange={(e) => setScenario(String(e.target.value))} sx={{ mb: 1.5 }}>
            <MenuItem value="Phiên hòa giải lần 1">Phiên hòa giải lần 1</MenuItem>
            <MenuItem value="Kiểm tra chứng cứ">Kiểm tra chứng cứ</MenuItem>
            <MenuItem value="Phiên xét xử sơ thẩm">Phiên xét xử sơ thẩm</MenuItem>
          </Select>

          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Vai trò AI đối phương
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <RoleBtn active={aiRole === 1} onClick={() => setAiRole(1)}>
              AI Bị đơn
            </RoleBtn>
            <RoleBtn active={aiRole === 2} onClick={() => setAiRole(2)}>
              AI Thẩm phán
            </RoleBtn>
          </Stack>

          <Button
            variant="contained"
            fullWidth
            onClick={startTrial}
            sx={trialOpen ? { bgcolor: theme.palette.error.main, borderColor: theme.palette.error.main, '&:hover': { bgcolor: theme.palette.error.dark } } : undefined}
          >
            {trialOpen ? 'Dừng phiên' : 'Bắt đầu phiên tòa giả lập'}
          </Button>

          {trialOpen && (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 1.25 }} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Phiên tòa đang chạy:
              </Typography>
              <Stack spacing={1}>
                {chatMessages.map((m) => (
                  <ChatBubble key={m.id} tone={m.tone}>
                    {m.text}
                  </ChatBubble>
                ))}
                {chatMessages.length < script.length && (
                  <ChatBubble tone="ai">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PulseDot />
                      <Typography variant="body2">AI đang phản hồi...</Typography>
                    </Stack>
                  </ChatBubble>
                )}
              </Stack>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  )
}

function PipeNode({ icon, title, done, pending, syncing }: { icon: string; title: string; done?: boolean; pending?: boolean; syncing?: boolean }) {
  const theme = useTheme()
  const bg = done ? alpha(theme.palette.success.main, 0.12) : syncing ? alpha(theme.palette.info.main, 0.12) : 'background.paper'
  const border = done ? alpha(theme.palette.success.main, 0.35) : alpha(theme.palette.divider, 1)
  const badgeBg = done ? alpha(theme.palette.success.main, 0.12) : pending ? alpha(theme.palette.warning.main, 0.18) : alpha(theme.palette.info.main, 0.12)
  const badgeFg = done ? theme.palette.success.dark : pending ? theme.palette.warning.dark : theme.palette.info.dark
  const badgeText = done ? 'Đã đồng bộ' : syncing ? 'Đang đồng bộ...' : 'Chờ đồng bộ'

  return (
    <Stack alignItems="center" sx={{ minWidth: 120 }}>
      <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: border, bgcolor: bg }}>
        <Typography>{icon}</Typography>
      </Box>
      <Typography variant="caption" sx={{ textAlign: 'center', color: done ? theme.palette.success.dark : 'text.secondary', mt: 0.5 }}>
        {title}
      </Typography>
      <Box component="span" sx={{ mt: 0.5, px: 1, py: 0.25, borderRadius: 1, bgcolor: badgeBg, color: badgeFg, fontSize: 10, fontWeight: 700 }}>
        {badgeText}
      </Box>
    </Stack>
  )
}

function PipeLine({ done, syncing }: { done?: boolean; syncing?: boolean }) {
  const theme = useTheme()
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 48,
        height: 2,
        mt: 2.25,
        bgcolor: done ? theme.palette.primary.main : alpha(theme.palette.divider, 1),
        position: 'relative',
        opacity: syncing ? 0.7 : 1,
        transition: 'background-color 0.2s',
        '&:after': { content: '""', position: 'absolute', right: -6, top: -4, border: '5px solid transparent', borderLeftColor: done ? theme.palette.primary.main : alpha(theme.palette.divider, 1) },
      }}
    />
  )
}

function StatCard({ value, label }: { value: string; label: string }) {
  const theme = useTheme()
  return (
    <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: alpha(theme.palette.grey[500], 0.06) }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  )
}

function RoleBtn({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  const theme = useTheme()
  return (
    <Button
      size="small"
      variant="outlined"
      onClick={onClick}
      sx={
        active
          ? { bgcolor: alpha(theme.palette.info.main, 0.12), borderColor: theme.palette.primary.main, color: theme.palette.primary.main }
          : undefined
      }
    >
      {children}
    </Button>
  )
}

function ChatBubble({ tone, children }: { tone: 'ai' | 'user'; children: React.ReactNode }) {
  const theme = useTheme()
  const isUser = tone === 'user'
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: isUser ? alpha(theme.palette.info.main, 0.18) : alpha(theme.palette.grey[500], 0.08),
        color: isUser ? '#042C53' : 'text.primary',
        maxWidth: '85%',
        alignSelf: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
        {children}
      </Typography>
    </Box>
  )
}

function PulseDot() {
  const theme = useTheme()
  return (
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        bgcolor: theme.palette.primary.main,
        animation: 'pulse 0.8s ease-in-out infinite alternate',
        '@keyframes pulse': { from: { opacity: 0.3, transform: 'scale(0.8)' }, to: { opacity: 1, transform: 'scale(1)' } },
      }}
    />
  )
}
